import React, { useState, useEffect } from 'react';
import axios from 'axios';
import KaTeXContent from './KaTeXContent';
import RefreshActionButton from './RefreshActionButton';
import { useConfirm } from '../../contexts/ConfirmContext';
import { saveWorksheetAsPDF } from '../../utils/worksheetExport';
import NoteSlidesViewer from '../notes/NoteSlidesViewer';
import NotesEditorModal from '../notes/NotesEditorModal';
import VideoPlayer from '../video/VideoPlayer';
import VideoEditorPanel from '../video/VideoEditorPanel';
import VideoExportButton from '../video/VideoExportButton';
import QuizFlashcards from './QuizFlashcards';
import QuizFlashcardEditorModal from './QuizFlashcardEditorModal';
import { normalizeGrade, truncateTaskTitle, resolveApiFileUrl } from '../../utils/formatters';

const StudySupportSection = ({
  user,
  activeChild,
  filterByRole = null,
  selectedSubject = null,
  activeBatch = null,
  taskRefreshKey = 0,
  allowNoteEditing = true,
  selectedGrade = '',
  allowActions = true,
}) => {
  const [assignments, setAssignments] = useState([]);
  const [videoAssignments, setVideoAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedNote, setSelectedNote] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [videoLoadingId, setVideoLoadingId] = useState(null);
  const [showAllMaterials, setShowAllMaterials] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [isCopyingLink, setIsCopyingLink] = useState(false);
  const [accessLinkDialog, setAccessLinkDialog] = useState(null);
  const [showEditNoteModal, setShowEditNoteModal] = useState(false);
  const [saveEditLoading, setSaveEditLoading] = useState(false);
  const [showEditVideoPanel, setShowEditVideoPanel] = useState(false);
  const [videoForEdit, setVideoForEdit] = useState(null);
  const [showNoteFlashcards, setShowNoteFlashcards] = useState(false);
  const [showEditFlashcardsModal, setShowEditFlashcardsModal] = useState(false);
  const confirm = useConfirm();
  
  console.log('[Study Support] Component rendered. User role:', user?.role, 'Active child:', activeChild?.id);

  const loadAssignments = async () => {
    console.log('[Study Support] Loading assignments for user:', user?.id, 'activeChild:', activeChild?.id);
    try {
      setLoading(true);
      const [notesRes, videosRes] = await Promise.allSettled([
        axios.get('/notes/assigned'),
        axios.get('/videos/assigned'),
      ]);
      if (notesRes.status === 'fulfilled') {
        setAssignments(notesRes.value.data.assignments || []);
      }
      if (videosRes.status === 'fulfilled') {
        const rawVideoAssignments = videosRes.value.data.assignments || [];
        setVideoAssignments(rawVideoAssignments.map((a) => ({ ...a, _item_type: 'video' })));
      }
    } catch (error) {
      console.error('Error loading study materials:', error);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await loadAssignments();
  };

  useEffect(() => {
    loadAssignments();
  }, [activeChild, user, filterByRole, activeBatch?.id]);

  useEffect(() => {
    if (taskRefreshKey > 0) {
      loadAssignments();
    }
  }, [taskRefreshKey]);

  const handleMarkRead = async (assignmentId, isRead) => {
    try {
      console.log('[Study Support] Marking assignment:', assignmentId, 'Current is_read:', isRead, 'Will toggle to:', !isRead);
      const response = await axios.post(`/notes/assignment/${assignmentId}/mark-read?is_read=${!isRead}`);
      console.log('[Study Support] Mark read response:', response.data);
      console.log('[Study Support] Updated assignment:', response.data.assignment);
      
      // Update local state
      setAssignments(prevAssignments => {
        const updated = prevAssignments.map(a => 
          a.id === assignmentId ? {...a, is_read: !isRead, read_at: !isRead ? new Date().toISOString() : null} : a
        );
        console.log('[Study Support] Updated assignments state. Assignment', assignmentId, 'is now:', updated.find(a => a.id === assignmentId)?.is_read ? 'READ' : 'UNREAD');
        return updated;
      });
    } catch (error) {
      console.error('[Study Support] Error updating read status:', error);
      console.error('[Study Support] Error details:', error.response?.data);
    }
  };

  const handleDeleteAssignment = async (assignment) => {
    const confirmationMessage = ['coaching_teacher', 'school_teacher'].includes(user?.role)
      ? 'Delete this note for all assigned students?'
      : 'Delete this note?';

    const confirmed = await confirm(confirmationMessage);
    if (!confirmed) return;

    try {
      await axios.delete(`/notes/assignment/${assignment.id}`);

      setAssignments(prevAssignments => prevAssignments.filter(item => {
        const belongsToUser = item.assigned_by === user?.id;
        const sameNote = item.note_id === assignment.note_id;

        // Coaching teachers and parents remove all instances of the same note they created
        if (belongsToUser && sameNote) {
          return false;
        }

        return item.id !== assignment.id;
      }));

      if (selectedNote && (selectedNote.id === assignment.id || selectedNote.note_id === assignment.note_id)) {
        setSelectedNote(null);
      }
    } catch (error) {
      console.error('[Study Support] Error deleting assignment:', error);
      console.error('[Study Support] Error details:', error.response?.data);
      alert('Unable to delete this note. Please try again.');
    }
  };

  const handleMarkVideoRead = async (assignmentId, isRead) => {
    try {
      await axios.post(`/videos/assignment/${assignmentId}/mark-read?is_read=${!isRead}`);
      setVideoAssignments((prev) =>
        prev.map((a) =>
          a.id === assignmentId
            ? { ...a, is_read: !isRead, read_at: !isRead ? new Date().toISOString() : null }
            : a
        )
      );
    } catch (error) {
      console.error('[Study Support] Error updating video read status:', error);
    }
  };

  const handleDeleteVideoAssignment = async (assignment) => {
    const confirmed = await confirm(
      ['coaching_teacher', 'school_teacher'].includes(user?.role)
        ? 'Delete this video for all assigned students?'
        : 'Delete this video assignment?'
    );
    if (!confirmed) return;
    try {
      await axios.delete(`/videos/assignment/${assignment.id}`);
      setVideoAssignments((prev) =>
        prev.filter((a) => {
          if (a.assigned_by === user?.id && a.video_id === assignment.video_id) return false;
          return a.id !== assignment.id;
        })
      );
      if (selectedVideo && selectedVideo.id === assignment.id) setSelectedVideo(null);
    } catch (error) {
      console.error('[Study Support] Error deleting video assignment:', error);
      alert('Unable to delete this video assignment. Please try again.');
    }
  };

  const handleViewVideo = async (assignment) => {
    if (!assignment.is_read && user?.role === 'parent') {
      handleMarkVideoRead(assignment.id, assignment.is_read);
    }
    const videoId = assignment.video_id || assignment.video_details?.video_id;
    if (!videoId) return;
    setVideoLoadingId(assignment.id);
    try {
      const res = await axios.get(`/videos/${videoId}`);
      setSelectedVideo({ ...assignment, _full_video: res.data });
    } catch (err) {
      console.error('[Study Support] Error fetching video:', err);
      alert('Unable to load this video. Please try again.');
    } finally {
      setVideoLoadingId(null);
    }
  };

  const handleViewNote = async (assignment) => {
    console.log('[Study Support] Viewing note. Assignment:', assignment.id, 'Current is_read:', assignment.is_read, 'User role:', user?.role);
    setSelectedNote(assignment);
    setShowEditNoteModal(false);
    // Flashcard-only notes have no body to read first — jump straight into the quiz.
    setShowNoteFlashcards(assignment.note_type === 'quiz');
    setShowEditFlashcardsModal(false);
    // Mark as read when viewing (only for children viewing their own assignments)
    if (!assignment.is_read && user?.role === 'parent') {
      console.log('[Study Support] Will mark as read for student');
      await handleMarkRead(assignment.id, assignment.is_read);
      // Update the selected note's read status for the modal
      setSelectedNote({...assignment, is_read: true, read_at: new Date().toISOString()});
    }
  };

  const handleSaveFlashcards = async (questions) => {
    if (!selectedNote) return;
    const noteId = selectedNote.note_id || selectedNote.note_details?.id;
    if (!noteId) {
      alert('Unable to update quiz because note id is missing.');
      return;
    }

    try {
      const response = await axios.put(`/notes/${noteId}`, { flashcards: questions });
      const saved = Array.isArray(response.data?.flashcards) ? response.data.flashcards : questions;

      setAssignments((prevAssignments) => prevAssignments.map((assignment) => {
        const isSameNote = assignment.note_id === selectedNote.note_id || assignment.id === selectedNote.id;
        if (!isSameNote) return assignment;
        return {
          ...assignment,
          note_details: {
            ...(assignment.note_details || {}),
            flashcards: saved,
          },
        };
      }));

      setSelectedNote((prev) => prev ? {
        ...prev,
        note_details: {
          ...(prev.note_details || {}),
          flashcards: saved,
        },
      } : prev);

      setShowEditFlashcardsModal(false);
      alert('Quiz updated successfully');
    } catch (error) {
      console.error('[Study Support] Error saving quiz:', error);
      alert('Failed to save quiz');
      throw error;
    }
  };

  // Records a completed quiz attempt so progress is visible next time this
  // note is opened (by the student, and by whoever assigned it). Only
  // students "complete" a quiz — a parent/teacher opening the same note
  // here is previewing it, not learning from it, so their attempts aren't
  // recorded. Best-effort: a failed request shouldn't interrupt the child's
  // flow with an error dialog over something this incidental.
  const handleQuizComplete = async ({ score, total }) => {
    if (user?.role !== 'student' || !selectedNote) return;
    const noteId = selectedNote.note_id || selectedNote.note_details?.id;
    if (!noteId) return;

    try {
      const response = await axios.post(`/notes/${noteId}/quiz-result`, { score, total });
      const result = response.data?.result || { score, total, completed_at: new Date().toISOString() };

      setAssignments((prevAssignments) => prevAssignments.map((assignment) => {
        const isSameNote = assignment.note_id === selectedNote.note_id || assignment.id === selectedNote.id;
        return isSameNote ? { ...assignment, last_quiz_result: result } : assignment;
      }));
      setSelectedNote((prev) => (prev ? { ...prev, last_quiz_result: result } : prev));
    } catch (error) {
      console.error('[Study Support] Error recording quiz result:', error);
    }
  };

  const handleEditVideo = async (assignment) => {
    const videoId = assignment.video_id || assignment.video_details?.video_id;
    if (!videoId) return;
    setVideoLoadingId(assignment.id);
    try {
      const res = await axios.get(`/videos/${videoId}`);
      setVideoForEdit(res.data);
      setShowEditVideoPanel(true);
    } catch (err) {
      console.error('[Study Support] Error fetching video for edit:', err);
      alert('Unable to load this video for editing. Please try again.');
    } finally {
      setVideoLoadingId(null);
    }
  };

  const buildNotePreviewText = (html, fallback = '') => {
    if (!html) return fallback;

    const temp = document.createElement('div');
    temp.innerHTML = html;

    temp.querySelectorAll('script, style, meta, title, link').forEach((node) => node.remove());

    const text = (temp.textContent || temp.innerText || '').replace(/\s+/g, ' ').trim();
    if (!text) return fallback;

    const maxLength = 20;
    return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
  };

  const sanitizeNotesForExport = (html = '') => {
    if (!html || typeof html !== 'string') return '';

    const temp = document.createElement('div');
    temp.innerHTML = html;
    const textMatcher = /\b(task\s*code|topics?)\b\s*:/i;

    temp.querySelectorAll('p,div,li,h1,h2,h3,h4,h5,h6,span,strong').forEach((node) => {
      const text = (node.textContent || '').replace(/\s+/g, ' ').trim();
      if (textMatcher.test(text)) {
        node.remove();
      }
    });

    return temp.innerHTML
      .replace(/Task\s*Code\s*:\s*[A-Z0-9-]+/gi, '')
      .replace(/Topics?\s*:\s*[^<\n]+/gi, '')
      .trim();
  };

  const canEditSelectedNote = Boolean(allowNoteEditing) && selectedNote && (() => {
    const allowedEditorRoles = ['parent', 'coaching_teacher', 'school_teacher'];
    if (!allowedEditorRoles.includes(user?.role)) {
      return false;
    }

    const ownerCandidates = [
      selectedNote.assigned_by,
      selectedNote.owner_id,
      selectedNote.created_by,
      selectedNote.creator_id,
      selectedNote.note_details?.assigned_by,
      selectedNote.note_details?.owner_id,
      selectedNote.note_details?.created_by,
      selectedNote.note_details?.creator_id
    ].filter((value) => value !== undefined && value !== null);

    return ownerCandidates.some((ownerId) => String(ownerId) === String(user?.id));
  })();

  const handleOpenEditNote = () => {
    if (!selectedNote || !canEditSelectedNote) return;
    setShowEditNoteModal(true);
  };

  const handleSaveEditNote = async (updatedContent) => {
    if (!selectedNote) return;

    const noteId = selectedNote.note_id || selectedNote.note_details?.id;
    if (!noteId) {
      alert('Unable to update this note because note id is missing.');
      return;
    }

    setSaveEditLoading(true);
    try {
      await axios.put(`/notes/${noteId}`, {
        html_content: updatedContent
      });

      const previewText = ''; // buildNotePreviewText(updatedContent, selectedNote.description || '');

      setAssignments((prevAssignments) => prevAssignments.map((assignment) => {
        const isSameNote = assignment.note_id === selectedNote.note_id || assignment.id === selectedNote.id;
        if (!isSameNote) return assignment;

        return {
          ...assignment,
          description: previewText,
          note_details: {
            ...(assignment.note_details || {}),
            html_content: updatedContent
          }
        };
      }));

      setSelectedNote((prev) => prev ? {
        ...prev,
        description: previewText,
        note_details: {
          ...(prev.note_details || {}),
          html_content: updatedContent
        }
      } : prev);

      setShowEditNoteModal(false);
      alert('Notes updated successfully');
    } catch (error) {
      console.error('Error saving notes:', error);
      alert('Failed to save notes');
    } finally {
      setSaveEditLoading(false);
    }
  };

  const handleDownloadPDF = async (note) => {
    const content = sanitizeNotesForExport(note.note_details?.html_content || note.description || '<p>No content available</p>');
    const assignedDate = note.assigned_at ? new Date(note.assigned_at).toLocaleDateString() : new Date().toLocaleDateString();

    try {
      await saveWorksheetAsPDF({
        title: note.title || 'Notes',
        content,
        grade: note.grade || note.note_details?.grade || activeChild?.grade || 'N/A',
        subject: note.subject || 'General',
        topics: '',
        date: assignedDate,
        taskCode: '',
        fileName: note.title || 'notes',
      });
    } catch (error) {
      console.error('Error saving note PDF:', error);
      alert('Unable to save the note as PDF. Please try printing instead.');
    }
  };

  const handleSaveNote = (note) => {
    const content = sanitizeNotesForExport(note.note_details?.html_content || note.description || '');
    const fileName = (note.title || 'notes').replace(/[^a-z0-9-_]+/gi, '_');
    const element = document.createElement('a');
    const file = new Blob([content], { type: 'text/html' });
    element.href = URL.createObjectURL(file);
    element.download = `${fileName}.html`;
    element.click();
    URL.revokeObjectURL(element.href);
  };

  const copyToClipboardWithFallback = async (text) => {
    if (!text) return false;

    if (navigator?.clipboard?.writeText && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch (clipboardError) {
        console.warn('Clipboard API copy failed, falling back to execCommand:', clipboardError);
      }
    }

    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.top = '-9999px';
      textarea.style.left = '-9999px';
      textarea.style.opacity = '0';

      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      textarea.setSelectionRange(0, textarea.value.length);

      const isCopied = document.execCommand('copy');
      document.body.removeChild(textarea);
      return isCopied;
    } catch (fallbackError) {
      console.error('Fallback copy failed:', fallbackError);
      return false;
    }
  };

  const handleCopyGuestLink = async (event, linkUrl) => {
    event.stopPropagation();
    if (!linkUrl) return;

    setIsCopyingLink(true);
    try {
      const copied = await copyToClipboardWithFallback(linkUrl);
      setAccessLinkDialog({
        url: linkUrl,
        copied
      });
    } catch (error) {
      console.error('Error preparing guest notes access link:', error);
      setAccessLinkDialog({
        url: linkUrl,
        copied: false
      });
    } finally {
      setIsCopyingLink(false);
    }
  };

  // ── Note assignments filtering ──
  let filteredNotes = user?.role === 'parent' && activeChild
    ? assignments.filter(a => a.assigned_to === activeChild.id)
    : assignments;
  
  // Apply role filter if specified (for parent dashboard space filtering)
  if (filterByRole) {
    filteredNotes = filteredNotes.filter(a => a.assigned_by_role === filterByRole);
  }
  
  // Apply subject filter if specified
  if (selectedSubject && selectedSubject !== 'All Subjects') {
    filteredNotes = filteredNotes.filter(a => a.subject === selectedSubject);
  }
  
  // Apply batch filter for coaching teachers — same logic as "Your videos"
  // in VideoCreatorModal: match the active batch's subject instead of
  // re-deriving "is this student currently in the batch's roster", which
  // showed an item under every batch a student happens to share
  // membership with. Grade is deliberately NOT part of this match: unlike
  // subject (which the note/video's own subject field always carries
  // from generation time), the `grade` stored on an assignment is the
  // assigned child's own profile grade, not the batch's grade — matching
  // on it hid every assignment whenever a student's profile grade didn't
  // line up with the batch's grade.
  if (['coaching_teacher', 'school_teacher'].includes(user?.role) && activeBatch?.id) {
    const batchSubject = (activeBatch.subject || '').trim().toLowerCase();
    filteredNotes = filteredNotes.filter(a => (
      !batchSubject || (a.subject || '').trim().toLowerCase() === batchSubject
    ));
  }

  // Apply grade filter if specified (grade pill selection in dashboards).
  // Skipped for Coaching/School Teacher space: a batch's grade can
  // legitimately differ from a student's own profile grade, so matching on
  // it hid every note/video whenever the two didn't line up. `filterByRole`
  // carries this when Study Support is embedded in another role's dashboard
  // (e.g. the Parent view's role-toggle); on a teacher's own dashboard
  // `filterByRole` is never set, so fall back to the logged-in user's role.
  const isTeacherOwnSpace = !filterByRole && ['coaching_teacher', 'school_teacher'].includes(user?.role);
  if (selectedGrade && filterByRole !== 'coaching_teacher' && !isTeacherOwnSpace) {
    const normalizedSelectedGrade = normalizeGrade(selectedGrade);
    filteredNotes = filteredNotes.filter((a) => {
      const g = a.note_details?.grade ?? a.grade;
      return normalizeGrade(g) === normalizedSelectedGrade;
    });
  }

  if (['coaching_teacher', 'school_teacher'].includes(user?.role)) {
    const uniqueNotes = new Map();
    filteredNotes.forEach(a => {
      const key = a.note_id;
      const existing = uniqueNotes.get(key);
      if (!existing) { uniqueNotes.set(key, a); return; }
      if (!existing.guest_access_link && a.guest_access_link) uniqueNotes.set(key, a);
    });
    filteredNotes = Array.from(uniqueNotes.values());
  }

  // ── Video assignments filtering ──
  let filteredVideos = user?.role === 'parent' && activeChild
    ? videoAssignments.filter(a => a.assigned_to === activeChild.id)
    : videoAssignments;

  if (filterByRole) {
    filteredVideos = filteredVideos.filter(a => a.assigned_by_role === filterByRole);
  }
  if (selectedSubject && selectedSubject !== 'All Subjects') {
    filteredVideos = filteredVideos.filter(a => a.subject === selectedSubject);
  }
  if (['coaching_teacher', 'school_teacher'].includes(user?.role) && activeBatch?.id) {
    const batchSubject = (activeBatch.subject || '').trim().toLowerCase();
    filteredVideos = filteredVideos.filter(a => (
      !batchSubject || (a.subject || '').trim().toLowerCase() === batchSubject
    ));
  }
  if (selectedGrade && filterByRole !== 'coaching_teacher' && !isTeacherOwnSpace) {
    const normalizedSelectedGrade = normalizeGrade(selectedGrade);
    filteredVideos = filteredVideos.filter((a) => {
      const g = a.video_details?.grade ?? a.grade;
      return normalizeGrade(g) === normalizedSelectedGrade;
    });
  }
  if (['coaching_teacher', 'school_teacher'].includes(user?.role)) {
    const uniqueVideos = new Map();
    filteredVideos.forEach(a => {
      if (!uniqueVideos.has(a.video_id)) uniqueVideos.set(a.video_id, a);
    });
    filteredVideos = Array.from(uniqueVideos.values());
  }

  // ── Combine notes + videos, notes first ──
  let filteredAssignments = [
    ...filteredNotes.map(a => ({ ...a, _item_type: 'note' })),
    ...filteredVideos,
  ];

  const normalizedSearch = searchTerm.trim().toLowerCase();
  if (normalizedSearch) {
    filteredAssignments = filteredAssignments.filter(a => (
      a.title?.toLowerCase().includes(normalizedSearch) ||
      a.description?.toLowerCase().includes(normalizedSearch) ||
      a.subject?.toLowerCase().includes(normalizedSearch) ||
      a.assigned_by_name?.toLowerCase().includes(normalizedSearch)
    ));
  }

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        <p className="text-gray-600 mt-2">Loading study materials...</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-0 sm:gap-3">
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
          📚 Study Support
        </h2>
        <div className="flex items-center space-x-1 sm:space-x-3 justify-end sm:justify-start">
          <span className="text-xs text-gray-500">
            <span>{filteredAssignments.length} material{filteredAssignments.length !== 1 ? 's' : ''}</span>
          </span>
          <button
            type="button"
            onClick={() => {
              setShowSearch((prev) => {
                if (prev) {
                  setSearchTerm('');
                }
                return !prev;
              });
            }}
            className="text-xs px-1 py-1 sm:px-2 sm:py-2 rounded-lg border sm:text-xs border-gray-200 text-gray-700 hover:bg-gray-100"
          >
            <span>🔍</span>
            <span className="hidden sm:inline ml-1">Filter</span>
          </button>
          <RefreshActionButton
            onClick={loadAssignments}
            disabled={loading || isRefreshing}
          />
        </div>
      </div>

      {showSearch && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-0 sm:gap-3">
          <div className="relative flex-1">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search study support..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-2.5 text-xs text-gray-500 hover:text-gray-700"
              >
                Clear
              </button>
            )}
          </div>
          {searchTerm && (
            <span className="text-xs text-gray-500">
              Showing results for "{searchTerm}"
            </span>
          )}
        </div>
      )}

      {filteredAssignments.length === 0 ? (
        <div className="text-center py-12 bg-green-50 rounded-lg">
          <div className="text-xl sm:text-4xl mb-0 sm:mb-2">📚</div>
          <p className="text-gray-600">
            {searchTerm ? 'No study materials match your search' : 'No study materials yet'}
          </p>
          <p className="text-xs sm:text-sm text-gray-500 mt-1">
            {searchTerm ? 'Try adjusting your search or clearing the filter' : 'Materials will appear here when assigned'}
          </p>
        </div>
      ) : (
        <>
          <div className="rounded-lg min-h-[150px] max-h-[250px] overflow-y-auto">
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            {(showAllMaterials ? filteredAssignments : filteredAssignments.slice(0, 5)).map((assignment) => {
              const isVideo = assignment._item_type === 'video';
              const isLoadingThis = isVideo && videoLoadingId === assignment.id;
              return (
            <div
              key={assignment.id}
              className={`border rounded-lg p-2 sm:p-4 ${assignment.is_read ? 'bg-blue-50 border-blue-200' : 'bg-purple-50 border-purple-200'} hover:shadow-md transition-shadow cursor-pointer h-full`}
              onClick={() => isVideo ? handleViewVideo(assignment) : handleViewNote(assignment)}
            >
              <div className="flex flex-col h-full gap-1 sm:gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-1 sm:gap-3">
                    {isVideo && <span className="text-xs">🎬</span>}
                    {isVideo && (
                      <span className="px-2 py-1 bg-yellow-200 text-yellow-700 text-[10px] rounded-full">Video</span>
                    )}
                    {!isVideo && <span className="text-xs">{assignment.note_type === 'quiz' ? '🎴' : '📝'}</span>}
                    {!isVideo && (
                      <span className="px-2 py-1 bg-purple-200 text-purple-700 text-[10px] rounded-full">
                        {assignment.note_type === 'quiz' ? 'Quiz' : 'Notes'}
                      </span>
                    )}
                    <h3 className="text-sm sm:text-lm font-semibold text-gray-900 break-words">{truncateTaskTitle(assignment.title)}</h3>
                    {!assignment.is_read && (
                      <span className="px-2 py-1 bg-blue-600 text-white text-[10px] rounded-full">New</span>
                    )}
                    {assignment.last_quiz_result && (
                      <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-[10px] rounded-full">
                        🏆 {assignment.last_quiz_result.score}/{assignment.last_quiz_result.total}
                      </span>
                    )}
                  </div>
                  {isVideo && assignment.video_details && (
                    <p className="text-xs text-gray-500 mt-1">{assignment.video_details.total_slides} slides</p>
                  )}
                  <p className="text-xs sm:text-xs text-gray-600 mt-1 break-words">{assignment.description}</p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-[10px] sm:text-xs text-gray-500">
                    {/*<span className="hidden sm:inline">📖 {assignment.subject}</span>
                    <span className="hidden sm:inline">👤 {assignment.assigned_by_name}</span>*/}
                    <span>🕐 {new Date(assignment.assigned_at).toLocaleDateString()}</span>
                    {/*{assignment.is_read && (
                      <span className="text-green-600 hidden sm:inline">✓ Read</span>
                    )}*/}
                    {isLoadingThis && <span className="text-violet-500">Loading…</span>}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-1 mt-auto">
                  {/* Only show Mark Read/Unread button for children viewing their own assignments */}
                  {/*{user?.role === 'parent' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isVideo) {
                          handleMarkVideoRead(assignment.id, assignment.is_read);
                        } else {
                          handleMarkRead(assignment.id, assignment.is_read);
                        }
                      }}
                      className="text-[10px] sm:text-xs text-blue-600 hover:text-blue-800"
                    >
                      {assignment.is_read ? 'Mark Unread' : 'Mark Read'}
                    </button>
                  )}*/}
                  {['coaching_teacher', 'school_teacher'].includes(user?.role) && assignment.guest_access_link && (
                    <button
                      type="button"
                      onClick={(event) => handleCopyGuestLink(event, assignment.guest_access_link)}
                      className="compact-action-btn px-1.5 py-2 sm:px-1.5 sm:py-2 text-[10px] sm:text-xs bg-green-200 text-green-800 rounded-lg hover:bg-green-300 text-xs"
                      disabled={isCopyingLink}
                    >
                      <span className="text-[10px] text-orange-800 inline sm:hidden">🔗</span>
                      <span className="text-xs text-orange-800 hidden sm:inline">🔗 Link</span>
                    </button>
                  )}
                  {allowActions && ['coaching_teacher', 'school_teacher', 'parent'].includes(user?.role) && assignment.assigned_by === user?.id && assignment.note_type !== 'quiz' && (
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (isVideo) {
                          handleEditVideo(assignment);
                        } else {
                          await handleViewNote(assignment);
                          setShowEditNoteModal(true);
                        }
                      }}
                      className="compact-action-btn px-1.5 py-2 sm:px-1.5 sm:py-2 bg-orange-200 text-orange-800 rounded-lg hover:bg-orange-300 text-xs"
                    >
                      <span className="text-[10px] text-orange-800 inline sm:hidden">✏️</span>
                      <span className="text-xs text-orange-800 hidden sm:inline">✏️ Edit</span>
                    </button>
                  )}
                  {allowActions && !isVideo && ['coaching_teacher', 'school_teacher', 'parent'].includes(user?.role) && assignment.assigned_by === user?.id && assignment.note_type === 'quiz' && (
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        await handleViewNote(assignment);
                        setShowEditFlashcardsModal(true);
                      }}
                      className="compact-action-btn px-1.5 py-2 sm:px-1.5 sm:py-2 bg-orange-200 text-orange-800 rounded-lg hover:bg-orange-300 text-xs"
                    >
                      <span className="text-[10px] text-orange-800 inline sm:hidden">✏️</span>
                      <span className="text-xs text-orange-800 hidden sm:inline">✏️ Edit</span>
                    </button>
                  )}
                  {allowActions && ['coaching_teacher', 'school_teacher', 'parent'].includes(user?.role) && assignment.assigned_by === user?.id && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isVideo) {
                          handleDeleteVideoAssignment(assignment);
                        } else {
                          handleDeleteAssignment(assignment);
                        }
                      }}
                      className="compact-action-btn px-1.5 py-2 sm:px-1.5 sm:py-2 bg-red-200 text-red-800 rounded-lg hover:bg-red-300 text-xs text-xs"
                    >
                      <span className="text-[10px] text-red-700 inline sm:hidden">🗑</span>
                      <span className="text-xs text-red-700 hidden sm:inline">🗑 Delete</span>
                    </button>
                  )}
                  {allowActions && isVideo && ['coaching_teacher', 'school_teacher', 'parent'].includes(user?.role) && assignment.assigned_by === user?.id && (
                    <span onClick={(e) => e.stopPropagation()}>
                      <VideoExportButton
                        videoId={assignment.video_id || assignment.video_details?.video_id}
                        videoTitle={assignment.title}
                        compact
                      />
                    </span>
                  )}
                </div>
              </div>
            </div>
              );
            })}
            </div>
          </div>
          
          {/* See More Button */}
          {filteredAssignments.length > 5 && (
            <button
              onClick={() => setShowAllMaterials(!showAllMaterials)}
              className="mt-4 w-full text-center text-blue-600 hover:text-blue-800 text-sm font-medium"
            >
              {showAllMaterials ? 'Show Less' : `See More (${filteredAssignments.length - 5} more)`}
            </button>
          )}
        </>
      )}

      {/* View Note Modal */}
      {accessLinkDialog?.url && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
            <h2 className="text-lg font-semibold text-gray-900">
              {accessLinkDialog.copied ? '✅ Link copied' : '🔗 Link ready'}
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Share this study support link with students.
            </p>
            <p className="mt-3 break-all rounded-lg bg-orange-50 p-3 text-sm text-orange-800 border border-orange-200">
              <a href={accessLinkDialog.url} className="text-orange-900 underline" target="_blank" rel="noreferrer">
                {accessLinkDialog.url}
              </a>
            </p>
            {!accessLinkDialog.copied && (
              <p className="mt-2 text-xs text-gray-500">
                Automatic copy may be blocked by your browser. You can still copy the link manually from above.
              </p>
            )}
            <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => setAccessLinkDialog(null)}
                className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-50"
              >
                Close
              </button>
              <button
                type="button"
                onClick={async () => {
                  const copied = await copyToClipboardWithFallback(accessLinkDialog.url);
                  setAccessLinkDialog((prev) => (prev ? { ...prev, copied } : prev));
                }}
                className="rounded-lg bg-orange-500 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-600"
              >
                {accessLinkDialog.copied ? 'Copy again' : 'Copy link'}
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedNote && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">{truncateTaskTitle(selectedNote.title)}</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    By {selectedNote.assigned_by_name} • {new Date(selectedNote.assigned_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {canEditSelectedNote && selectedNote.note_type !== 'quiz' && (
                    <button
                      onClick={handleOpenEditNote}
                      className="px-3 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 text-xs sm:text-sm flex items-center space-x-1"
                      title="Edit Notes"
                    >
                      <span>✏️</span>
                      <span>Edit</span>
                    </button>
                  )}

                  {selectedNote.note_type !== 'quiz' && (
                  <>
                  {/* Print Button */}
                  <button
                    onClick={() => {
                      const sanitizedContent = sanitizeNotesForExport(
                        selectedNote.note_details?.html_content || selectedNote.description || '<p>No content available</p>'
                      );
                      const printWindow = window.open('', '_blank');
                      if (!printWindow || !printWindow.document) {
                        alert('Unable to open print preview. Please allow pop-ups and try again.');
                        return;
                      }
                      const printHTML = `
                        <!DOCTYPE html>
                        <html>
                        <head>
                          <title>${selectedNote.title}</title>
                          <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/katex@0.16.9/dist/katex.min.css" crossorigin="anonymous">
                          <style>
                            body { font-family: Arial, sans-serif; margin: 20px; line-height: 1.6; }
                            .print-header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 10px; margin-bottom: 20px; }
                            .content { max-width: 800px; margin: 0 auto; }
                            @media print { body { margin: 0; } }
                          </style>
                        </head>
                        <body>
                          <div class="print-header">
                            <h1>${selectedNote.title}</h1>
                            <p>${selectedNote.subject} | ${selectedNote.note_type || 'Notes'}</p>
                            <p style="font-size: 0.9em; color: #666;">By ${selectedNote.assigned_by_name} | ${new Date(selectedNote.assigned_at).toLocaleDateString()}</p>
                          </div>
                          <div class="content">
                            ${sanitizedContent}
                          </div>
                          
                          <script src="https://cdn.jsdelivr.net/npm/katex@0.16.9/dist/katex.min.js" crossorigin="anonymous"></script>
                          <script src="https://cdn.jsdelivr.net/npm/katex@0.16.9/dist/contrib/auto-render.min.js" crossorigin="anonymous"></script>
                          <script>
                            // Wait for KaTeX to load, then render and print
                            window.addEventListener('load', function() {
                              if (window.renderMathInElement) {
                                renderMathInElement(document.body, {
                                  delimiters: [
                                    {left: '$$', right: '$$', display: true},
                                    {left: '$', right: '$', display: false},
                                    {left: '\\\\[', right: '\\\\]', display: true},
                                    {left: '\\\\(', right: '\\\\)', display: false}
                                  ],
                                  throwOnError: false
                                });
                                
                                // Wait a bit for rendering to complete, then print
                                setTimeout(function() {
                                  window.print();
                                }, 1000);
                              } else {
                                // If KaTeX failed to load, just print anyway
                                setTimeout(function() {
                                  window.print();
                                }, 500);
                              }
                            });
                          </script>
                        </body>
                        </html>
                      `;
                      printWindow.document.write(printHTML);
                      printWindow.document.close();
                    }}
                    className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-800 text-xs sm:text-sm flex items-center space-x-1"
                    title="Print"
                  >
                    <span>🖨️</span>
                    <span>Print</span>
                  </button>
                  
                  <button
                    onClick={() => handleSaveNote(selectedNote)}
                    className="px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-xs sm:text-sm flex items-center space-x-1"
                    title="Save"
                  >
                    <span>💾</span>
                    <span>HTML</span>
                  </button>

                  {['coaching_teacher', 'school_teacher', 'parent'].includes(user?.role) && (
                    <button
                      onClick={() => handleDownloadPDF(selectedNote)}
                      className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-xs sm:text-sm flex items-center space-x-1"
                      title="Download as PDF"
                    >
                      <span>📄</span>
                      <span>PDF</span>
                    </button>
                  )}
                  </>
                )}
                </div>
                <div>
                  <button
                    onClick={() => {
                      setSelectedNote(null);
                      setShowEditNoteModal(false);
                    }}
                    className="absolute right-4 top-4 text-gray-400 text-xl px-2 hover:text-gray-600"
                  >
                    ✕
                  </button>
                </div>
              </div>
            </div>

            {(() => {
              const noteFlashcards = Array.isArray(selectedNote.note_details?.flashcards)
                ? selectedNote.note_details.flashcards
                : [];
              const hasFlashcards = noteFlashcards.length > 0;
              const isFlashcardOnly = selectedNote.note_type === 'quiz';
              return (
                <div className="p-6">
                  {hasFlashcards && (
                    <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
                      <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                        <span className="flex items-center gap-1">
                          <span>🎴</span>
                          <span>Quiz ({noteFlashcards.length} cards)</span>
                        </span>
                        {selectedNote.last_quiz_result && (
                          <span className="text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-full px-2 py-0.5">
                            🏆 Last try: {selectedNote.last_quiz_result.score}/{selectedNote.last_quiz_result.total}
                          </span>
                        )}
                      </h3>
                      <div className="flex items-center gap-2">
                        {!isFlashcardOnly && (
                          <button
                            type="button"
                            onClick={() => setShowNoteFlashcards((prev) => !prev)}
                            className="px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-xs sm:text-sm font-semibold flex items-center gap-1"
                          >
                            <span>{showNoteFlashcards ? '📖' : '🎴'}</span>
                            <span>{showNoteFlashcards ? 'Back to Notes' : `Start Quiz (${noteFlashcards.length})`}</span>
                          </button>
                        )}
                        {canEditSelectedNote && (
                          <button
                            type="button"
                            onClick={() => setShowEditFlashcardsModal(true)}
                            className="text-xs font-semibold text-orange-700 bg-orange-100 hover:bg-orange-200 rounded-lg px-2 py-1"
                          >
                            ✏️ Edit Quiz
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {hasFlashcards && showNoteFlashcards ? (
                    <QuizFlashcards
                      questions={noteFlashcards}
                      onClose={isFlashcardOnly ? undefined : () => setShowNoteFlashcards(false)}
                      onComplete={handleQuizComplete}
                      previewMode={user?.role !== 'student'}
                    />
                  ) : selectedNote.file_urls && selectedNote.file_urls.length > 0 ? (
                    <div className="space-y-4">
                      <p className="text-gray-700">{selectedNote.description}</p>
                      <div className="border rounded-lg p-4 bg-gray-50">
                        <h3 className="font-semibold mb-3">Attached Files:</h3>
                        {selectedNote.files && selectedNote.files.length > 0 ? (
                          <div className="flex flex-wrap gap-3">
                            {selectedNote.files.map((file, idx) => {
                              const isPdf = file.content_type === 'application/pdf';
                              const fileUrl = resolveApiFileUrl(file.url);
                              return (
                                <a
                                  key={file.id || idx}
                                  href={fileUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="group"
                                  title={file.filename}
                                >
                                  {isPdf ? (
                                    <div className="flex flex-col items-center justify-center h-24 w-24 rounded-lg border border-gray-200 bg-white group-hover:bg-gray-100 text-center px-1">
                                      <span className="text-2xl">📄</span>
                                      <span className="text-[10px] text-gray-600 truncate w-full">{file.filename}</span>
                                    </div>
                                  ) : (
                                    <div className="flex flex-col items-center gap-1">
                                      <img
                                        src={fileUrl}
                                        alt={file.filename}
                                        className="h-24 w-24 object-cover rounded-lg border border-gray-200 group-hover:opacity-90"
                                      />
                                      <span className="text-[10px] text-gray-600 truncate w-24 text-center">{file.filename}</span>
                                    </div>
                                  )}
                                </a>
                              );
                            })}
                          </div>
                        ) : (
                          selectedNote.file_urls.map((url, idx) => (
                            <a
                              key={idx}
                              href={resolveApiFileUrl(url)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block text-blue-600 hover:text-blue-800 py-1"
                            >
                              📤 File {idx + 1}
                            </a>
                          ))
                        )}
                      </div>
                    </div>
                  ) : selectedNote.note_type === 'slides' && Array.isArray(selectedNote.note_details?.slides) && selectedNote.note_details.slides.length > 0 ? (
                    <NoteSlidesViewer cards={selectedNote.note_details.slides} />
                  ) : selectedNote.note_details ? (
                    <KaTeXContent
                      content={selectedNote.note_details.html_content}
                      className="prose max-w-none"
                    />
                  ) : (
                    <p className="text-gray-600">Loading note content...</p>
                  )}
                </div>
              );
            })()}

            <NotesEditorModal
              isOpen={showEditNoteModal}
              initialContent={selectedNote.note_details?.html_content || selectedNote.description || ''}
              isSaving={saveEditLoading}
              onClose={() => setShowEditNoteModal(false)}
              onSave={handleSaveEditNote}
            />

            {showEditFlashcardsModal && (
              <QuizFlashcardEditorModal
                initialQuestions={selectedNote.note_details?.flashcards || []}
                title="Edit quiz"
                onClose={() => setShowEditFlashcardsModal(false)}
                onSave={handleSaveFlashcards}
              />
            )}
          </div>
        </div>
      )}

      {/* Video Player Modal */}
      {selectedVideo?._full_video && (
        <VideoPlayer
          video={selectedVideo._full_video}
          onClose={() => setSelectedVideo(null)}
        />
      )}

      {/* Video Editor Panel */}
      {showEditVideoPanel && videoForEdit && (
        <VideoEditorPanel
          video={videoForEdit}
          showAssign={false}
          onClose={() => {
            setShowEditVideoPanel(false);
            setVideoForEdit(null);
          }}
          userRole={user?.role}
        />
      )}
    </div>
  );
};

// Task Selection Modal Component

export default StudySupportSection;
